import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BeakerIcon,
  BoltIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { useDispatch } from "react-redux";
import {
  DEFAULT_INTELLIGENT_EMAIL_PROMPT,
  IInvalidField,
  IPolicy,
  IPolicyResponseItem,
  objectsAreEqual,
  OrganisationFeatureFlags,
  PolicyCalculationType,
  PolicyType,
  isDefaultPolicyTitle,
  validatePolicySync,
  yupErrorMessageParse,
} from "shared";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router";
import { When } from "react-if";

import { UnsavedChangesModal } from "../../Modals/UnsavedChanges";
import {
  useDeletePolicyMutation,
  useSuggestPolicyTitleMutation,
  useUpdatePolicyMutation,
} from "../../../services/api/policyApi/policy";
import { Card } from "../../../../common/Atoms/Card";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { usePolicies } from "../../../hooks/usePolicies";
import { useCurrentGroup, useGroups } from "../../../hooks/useGroups";
import { Button } from "../../../../common/Atoms/Button";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { useUser } from "../../../hooks/useUser";
import { open as openAssistant } from "../../../slices/assistantDrawer";
import { ITab, Tabs } from "../../../../common/Atoms/Tabs";
import { classNames } from "../../../../common/lib/classNames";
import { LoadingOverlay } from "../../Molecules/LoadingOverlay";
import { RECORD_ASSIGNEE } from "../../Common/Assignee";

import { HandleChange, PolicyItem } from "./PolicyItem";
import { INTELLIGENT_DYNAMIC_TEMPLATE_ID } from "./PolicyForm/Fields/EmailTemplateSelector";
import { statementTemplateChoiceFields } from "./PolicyForm/Fields/StatementTemplateSelector";
import { PolicyTestViewRoot } from "./PolicyTestView/PolicyTestViewRoot";
import { PolicyActionsView } from "./PolicyActionsView";

export function PolicyPageRoot() {
  const { data: policies, isLoading: policiesLoading, isFetching: policiesFetching } = usePolicies();
  const { refetch } = useGroups();
  const group = useCurrentGroup();
  const { policyId } = useParams();
  const [currentPolicy, setCurrentPolicy] = useState<IPolicyResponseItem | null>(null);
  const [policyFormData, setPolicyFormData] = useState<IPolicyResponseItem | null>(null);
  const [formDataDirty, setFormDataDirty] = useState(false);
  const [currentSidebarTab, setCurrentSidebarTab] = useState(`actions`);
  const navigate = useNavigate();
  const currentOrg = useGetSelectedOrganisation();
  const dispatch = useDispatch();
  const { data: user } = useUser();
  // Same gate as the drawer launcher in Root: org flag, or an admin signed on as a user
  const assistantEnabled = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_ASSISTANT) || !!user?.adminOverride;

  // Hooks
  const [updatePolicy, {
    isLoading: saveLoading,
    reset: resetSave,
  }] = useUpdatePolicyMutation();
  const [deletePolicy, {
    isLoading: deleteLoading,
    reset: resetDelete,
  }] = useDeletePolicyMutation();
  const [suggestTitle, { isLoading: suggestLoading }] = useSuggestPolicyTitleMutation();
  const [suggestedTitle, setSuggestedTitle] = useState<string | null>(null);
  /** Title before the suggestion was applied, so it can be undone */
  const [renamedFrom, setRenamedFrom] = useState<string | null>(null);
  // Bumped per request so a slow response for an earlier policy (or earlier save) is dropped
  const suggestRequest = useRef(0);

  /** Ask the LLM for a name when the policy still has its default one. Failures are silent, the chip just does not show. */
  async function maybeSuggestTitle(pol: IPolicy | undefined) {
    const request = ++suggestRequest.current;

    setSuggestedTitle(null);

    if (!pol || pol.system || !isDefaultPolicyTitle(pol.title)) {
      return;
    }

    try {
      const { title } = await suggestTitle({ organisationId: pol.organisation_id, id: pol.id }).unwrap();

      if (request === suggestRequest.current) {
        setSuggestedTitle(title === pol.title ? null : title);
      }
    }
    catch (err) {
      // Rate limited (429) is expected and silent, the hint line just does not show
      if ((err as { status?: number })?.status !== 429) {
        console.warn(`Could not suggest a policy title`, err);
      }
    }
  }

  // On open
  useEffect(() => {
    setRenamedFrom(null);
    maybeSuggestTitle(currentPolicy?.policy);
  }, [currentPolicy?.policy?.id]);

  const sidebarTabs: ITab[] = useMemo(() => {
    return [
      {
        name: `Actions`,
        href: `actions`,
        onClick: () => setCurrentSidebarTab(`actions`),
        current: currentSidebarTab === `actions`,
        icon: BoltIcon,
      },
      {
        name: `Test & Preview`,
        href: `test`,
        onClick: () => setCurrentSidebarTab(`test`),
        current: currentSidebarTab === `test`,
        icon: BeakerIcon,
      },
    ];
  }, [currentSidebarTab]);

  useEffect(() => {
    if (policies?.length && policyId) {
      const currentPolicy = policies.find(pol => pol.policy.id === Number(policyId));

      if (currentPolicy) {
        setCurrentPolicy(currentPolicy);
        if (!policyFormData) {
          setPolicyFormData({ ...currentPolicy });
          setFormDataDirty(false);
        }
      }
    }
  }, [policies, policyId, policyFormData]);

  // Keep policy form data in sync
  useEffect(() => {
    if (currentPolicy) {
      setPolicyFormData({ ...currentPolicy });
    }
  }, [currentPolicy]);

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  /** Close the drawer, upon successful save */
  const [ invalids, setInvalids ] = useState<IInvalidField[]>([]);

  function updateFormData(update: IPolicyResponseItem) {
    setPolicyFormData(update);
  }

  // Validate on load
  useEffect(() => {
    if (policyFormData) {
      const pol = { ...policyFormData };

      validate(pol.policy);
    }
  }, [ policyFormData ]);

  // Each time policy, or formdata changes, check if the form is dirty
  useEffect(() => {
    if (!currentPolicy && !policyFormData) {
      return;
    }

    if (!currentPolicy?.policy || !policyFormData?.policy) {
      setFormDataDirty(false);

      return;
    }

    const isDirty = !objectsAreEqual(currentPolicy, policyFormData);
    setFormDataDirty(isDirty);
  }, [ currentPolicy, policyFormData, setFormDataDirty ]);

  function checkStateAndGoToGroup() {
    if (formDataDirty) {
      setShowUnsavedModal(true);

      return;
    }

    goToGroup();
  }

  function goToGroup() {
    navigate(`/${currentOrg?.id}/groups/${group?.groupFormData?.id}`);
  }

  async function onSaveAndGoBack() {
    const valid = validate({ ...policyFormData.policy });
    if (!formDataDirty) {
      // No changes, just go back
      goToGroup();

      return;
    }

    if (valid) {
      await save();
      setShowUnsavedModal(false);
      setFormDataDirty(false);
      goToGroup();
    }
    else {
      // Not valid, go back to form
      setShowUnsavedModal(false);
    }
  }

  function resetStates() {
    setShowUnsavedModal(false);
    setInvalids([]);
    resetSave();
    resetDelete();
  }

  function onCancelExit() {
    resetStates();
    setShowUnsavedModal(false);
  }

  function onConfirmExit() {
    resetStates();
    goToGroup();
  }

  function validate(pol: IPolicy) {
    try {
      const valid = validatePolicySync(pol, false);

      setInvalids([]);

      return valid;
    }
    catch (err) {
      console.error(err);

      const errorMessages = yupErrorMessageParse(err);

      if (Array.isArray(errorMessages)) {
        setInvalids(errorMessages);
      }
      else {
        setInvalids([{
          message: `An Unknown Error has occured.`,
        }]);
      }

      return false;
    }
  }

  async function savePolicy(pol: IPolicyResponseItem, suggest = true) {
    const valid = validate(pol.policy);

    if (valid) {
      // Update policy first before any adhoc actions
      const updated = await updatePolicy({
        id: pol.policy.id,
        organisationId: pol.policy.organisation_id,
        ...pol,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = updated.data as any;

      if (data?.success) {
        toast.success(data.success, {
          autoClose: 4000,
        });
      }

      if (!updated.error && suggest) {
        maybeSuggestTitle(pol.policy);
      }

      return !updated.error;
    }

    return false;
  }

  function save() {
    return savePolicy({ ...policyFormData });
  }

  async function applySuggestedTitle() {
    const pol = { ...policyFormData, policy: { ...policyFormData.policy, title: suggestedTitle } };

    setRenamedFrom(policyFormData.policy.title);
    setSuggestedTitle(null);
    setPolicyFormData(pol);

    await savePolicy(pol);
  }

  /** Put the default title back and re-offer the same suggestion, no new LLM call */
  function undoSuggestedTitle() {
    const applied = policyFormData.policy.title;
    const pol = { ...policyFormData, policy: { ...policyFormData.policy, title: renamedFrom } };

    setRenamedFrom(null);
    setSuggestedTitle(applied);
    setPolicyFormData(pol);

    return savePolicy(pol, false);
  }

  async function onDelete() {
    await deletePolicy({
      id: policyFormData.policy.id,
      organisationId: policyFormData.policy.organisation_id,
    }).unwrap();

    await refetch();

    goToGroup();
  }

  const modalTitle = (
    <span className={ `flex` }>
      <ExclamationCircleIcon
        className={ `h-6 w-6 text-red-500 mr-1` }
      />
      { `You have unsaved changes!` }
    </span>
  );

  const handleChange: HandleChange = (e, isGroupsChange) => {
    const update = isGroupsChange ?
    // Only update group IDS
      {
        ...policyFormData,
        groupIds: e.value as number[],
      } :
      // Otherwise update the policy object
      {
        ...policyFormData,
        policy: {
          ...policyFormData.policy,
          [e.name]: e.value,
        },
      };

    // Intelligent Dynamic is picked in the template dropdown but stored as ai_prompt
    if (!isGroupsChange && e.name === `template_id`) {
      const intelligentDynamic = e.value === INTELLIGENT_DYNAMIC_TEMPLATE_ID;

      update.policy.template_id = intelligentDynamic ? null : e.value as string;
      update.policy.ai_prompt = intelligentDynamic ? (policyFormData.policy.ai_prompt ?? DEFAULT_INTELLIGENT_EMAIL_PROMPT) : null;
    }

    // Invoice/contact assignee is picked in the assignee dropdown but stored as assign_to_record_assignee
    if (!isGroupsChange && e.name === `assignee_user_id`) {
      update.policy.assign_to_record_assignee = e.value === RECORD_ASSIGNEE;
      update.policy.assignee_user_id = update.policy.assign_to_record_assignee ? null : e.value as string;
    }

    // Organisation default / standard / a template is one dropdown but two stored fields
    if (!isGroupsChange && e.name === `statement_template_id`) {
      Object.assign(update.policy, statementTemplateChoiceFields(e.value));
    }

    if (update.policy.policy_type === PolicyType.STATEMENT_LATE_FEE) {
      if (update.policy.calculation_type === PolicyCalculationType.NOT_APPLICABLE) {
        update.policy.percent = 0;
      }
    }

    updateFormData(update);
  };

  if (!policyFormData || !policyFormData.policy) {
    if (policiesLoading || policiesFetching) {
      return (
        <LoadingOverlay
          loading={ policiesLoading || policiesFetching }
          message={ `Policy loading...` }
        >
          <Card>
            <div className={ `h-3/4` } />
          </Card>
        </LoadingOverlay>
      );
    }

    return (
      <div className={ `flex justify-center items-center mt-12` }>
        <Paragraph>
          { `Policy not found. Please go back to the group and try again.` }
        </Paragraph>
      </div>
    );
  }

  const { policy } = policyFormData;

  return (
    <div>
      { /* Breadcrumbs */ }
      <div className={ `mb-2 flex justify-between items-start` }>
        <p>
          <a
            onClick={ e => {
              e.preventDefault();
              checkStateAndGoToGroup();
            } }
            href={ `#` }
          >
            <Paragraph
              as={ `span` }
              variant={ `link` }
            >
              { group?.groupFormData?.title }
            </Paragraph>
          </a>
          <Paragraph
            as={ `span` }
          >
            { ` > ` }
            { `Policies` }
            { ` > ` }
            { policy?.title }
          </Paragraph>
        </p>

        <Button
          color={ `paidniceGreenOutline` }
          size={ `md` }
          onClick={ () => onSaveAndGoBack() }
        >
          { `Save & Go Back` }
        </Button>
      </div>

      <Card>
        <div className={ `grid grid-cols-1 lg:grid-cols-12` }>
          <div className={ `lg:col-span-7` }>

            <PolicyItem
              onSave={ save }
              onDelete={ onDelete }
              onTryAssistant={ assistantEnabled ? () => dispatch(openAssistant(`Help me update this policy: `)) : undefined }
              invalids={ invalids }
              loading={ saveLoading || deleteLoading }
              handleChange={ handleChange }
              policyFormData={ policyFormData }
              formDataDirty={ formDataDirty }
              hasCompletedActions={ currentPolicy?.hasCompletedActions || false }
              titleSuggestion={ {
                title: suggestedTitle,
                loading: suggestLoading,
                onApply: applySuggestedTitle,
                renamedFrom,
                onUndo: undoSuggestedTitle,
              } }
            />
            <UnsavedChangesModal
              show={ showUnsavedModal }
              onCancel={ onCancelExit }
              onConfirm={ onConfirmExit }
              onSaveAndExit={ onSaveAndGoBack }
              title={ modalTitle }
              message={ `Are you sure you want to exit and discard your changes?` }
            />
          </div>

          { /* Preview / Result Side side */ }
          <div className={ classNames(
            `lg:col-span-5 lg:ml-3 lg:border-l-slate lg:border-l lg:pl-3`,
            /* When in mobile, add a top spacing + divider */
            `border-t-slate border-t mt-3 lg:border-t-0 lg:mt-0`,
          ) }>
            <Tabs
              navigational={ false }
              tabs={ sidebarTabs }
              className={ `pb-0` }
            />
            <When condition={ currentSidebarTab === `actions` }>
              <PolicyActionsView
                policyData={ currentPolicy }
                formDataDirty={ formDataDirty }
              />
            </When>
            <When condition={ currentSidebarTab === `test` }>
              <PolicyTestViewRoot
                onSave={ save }
                formDataDirty={ formDataDirty }
                policyFormData={ policyFormData }
              />
            </When>
          </div>
        </div>
      </Card>
    </div>
  );
}
